import isEqual from 'lodash/isEqual';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createStore,
	type Action,
	createHook,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { ContainerProps, State, DropTarget } from './types.tsx';
// Exported for testing
export const initialState: State = {
	currentDropTarget: null,
	instanceId: 'pdnd-issue-table-rows',
	issueConnectionId: '',
	items: new Map(),
	onRowReorder: undefined,
};

const actions = {
	setDropTarget:
		(dropTarget: DropTarget | null): Action<State> =>
		({ setState, getState }) => {
			const currentDropTarget = getState().currentDropTarget;
			if (!isEqual(currentDropTarget, dropTarget)) {
				setState({
					currentDropTarget: dropTarget,
				});
			}
		},
	reorderRows:
		(sourceIndex: number, destinationIndex: number): Action<State> =>
		({ getState }) => {
			const { items, onRowReorder } = getState();
			onRowReorder?.({
				source: {
					issueIds: [items.get(sourceIndex) ?? ''],
					rowIndex: sourceIndex,
				},
				destination: {
					relativeToIssueId: items.get(destinationIndex) ?? '',
					rowIndex: destinationIndex,
				},
				edge: sourceIndex > destinationIndex ? 'top' : 'bottom',
			});
		},
	registerItem:
		(edgeIndex: number, issueId: string): Action<State> =>
		({ getState, setState }) => {
			const { items } = getState();
			setState({ items: items.set(edgeIndex, issueId) });
		},
	unregisterItem:
		(edgeIndex: number): Action<State> =>
		({ getState, setState }) => {
			const { items } = getState();
			if (items.delete(edgeIndex)) {
				setState({ items });
			}
		},
};

export const DraggableRowsContainer = createContainer<ContainerProps>({
	getIsConcurrentSafe: () => true,
});

export const Store = createStore({
	initialState,
	actions,
	containedBy: DraggableRowsContainer,
	handlers: {
		onInit:
			() =>
			({ setState }, { instanceId, onRowReorder, issueConnectionId }) => {
				setState({
					instanceId: instanceId ?? initialState.instanceId,
					onRowReorder,
					...(fg('jsc_list_reranking_gate_technical') ? { issueConnectionId } : {}),
				});
			},
		onContainerUpdate:
			(props, prevProps) =>
			({ setState }, { instanceId, onRowReorder, issueConnectionId }) => {
				if (fg('jira-issue-table-fix-reordering')) {
					if (
						props.instanceId !== prevProps.instanceId ||
						props.onRowReorder !== prevProps.onRowReorder ||
						(props.issueConnectionId !== prevProps.issueConnectionId &&
							fg('jsc_list_reranking_gate_technical'))
					) {
						setState({
							instanceId,
							onRowReorder,
							...(fg('jsc_list_reranking_gate_technical') ? { issueConnectionId } : {}),
						});
					}
				} else if (props.instanceId !== prevProps.instanceId) {
					setState({
						instanceId,
						onRowReorder,
						...(fg('jsc_list_reranking_gate_technical') ? { issueConnectionId } : {}),
					});
				}
			},
		onDestroy:
			() =>
			({ getState }) => {
				const { items } = getState();
				items.clear();
			},
	},
});

export const useDraggableRowsConnectionId = createHook(Store, {
	selector: ({ issueConnectionId }) => issueConnectionId,
});
